import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Alert, CircularProgress,
  Fade, Chip, Tooltip, IconButton, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import {
  Gift, Copy, Check, Users, DollarSign, Clock, CheckCircle2,
  XCircle, Send, Link2, Banknote
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const SectionCard = styled(Paper)(() => ({
  background: '#141926',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
  padding: 24,
  marginBottom: 24,
}));

const StatBox = styled(Box)(() => ({
  background: 'rgba(124,58,237,0.06)',
  border: '1px solid rgba(124,58,237,0.15)',
  borderRadius: 12,
  padding: 20,
  textAlign: 'center',
  flex: 1,
  minWidth: 140,
}));

export default function IndiqueGanhe() {
  const [affiliate, setAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState('');

  const [withdrawing, setWithdrawing] = useState(false);

  const [form, setForm] = useState({
    nome: '', email: '', whatsapp: '', chavePix: '', motivo: ''
  });

  const authHeader = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }), []);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/affiliates/status`, authHeader());
      if (res.data.success) {
        setAffiliate(res.data.affiliate);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => {
    fetchStatus();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setForm(prev => ({ ...prev, nome: user.nome || '', email: user.email || '' }));
  }, [fetchStatus]);

  const handleSubmit = async () => {
    if (!form.nome || !form.email || !form.whatsapp || !form.chavePix || !form.motivo) {
      setError('Preencha todos os campos');
      return;
    }
    setError(''); setSubmitting(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/affiliates/apply`, form, authHeader());
      if (res.data.success) {
        setSuccess(res.data.message);
        fetchStatus();
      } else {
        setError(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao enviar solicitação');
    } finally { setSubmitting(false); }
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleWithdraw = async () => {
    setError(''); setWithdrawing(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/affiliates/withdraw`, {}, authHeader());
      if (res.data.success) {
        setSuccess(res.data.message);
        fetchStatus();
      } else { setError(res.data.message); }
    } catch (err) { setError(err.response?.data?.message || 'Erro ao solicitar saque'); }
    finally { setWithdrawing(false); }
  };

  const getRefLink = () => {
    const base = window.location.origin;
    return `${base}/register?ref=${affiliate.refCode}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress size={32} sx={{ color: '#7C3AED' }} />
      </Box>
    );
  }

  const isPending = affiliate?.status === 'pending';
  const isApproved = affiliate?.status === 'approved';
  const isRejected = affiliate?.status === 'rejected';

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 780, mx: 'auto' }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Gift size={22} color="#7C3AED" />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#F9FAFB', letterSpacing: '-0.02em' }}>
              Indique e Ganhe
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            Ganhe <strong style={{ color: '#10B981' }}>10% de comissão</strong> sobre todos os valores gastos pelos seus indicados
          </Typography>
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, background: 'rgba(239,68,68,0.1)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.2)', '& .MuiAlert-icon': { color: '#EF4444' } }}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, background: 'rgba(16,185,129,0.1)', color: '#6EE7B7', border: '1px solid rgba(16,185,129,0.2)', '& .MuiAlert-icon': { color: '#10B981' } }}>{success}</Alert>}

        {/* Sem solicitação: Formulário */}
        {!affiliate && (
          <SectionCard elevation={0}>
            <Box sx={{ p: 3, mb: 3, borderRadius: 3, background: 'linear-gradient(135deg, rgba(124,58,237,0.08), rgba(16,185,129,0.08))', border: '1px solid rgba(124,58,237,0.15)', textAlign: 'center' }}>
              <Typography sx={{ color: '#F9FAFB', fontWeight: 700, fontSize: '1.2rem', mb: 1 }}>
                Seja um afiliado FTM
              </Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.9rem' }}>
                Indique amigos e ganhe <strong style={{ color: '#10B981' }}>10%</strong> de tudo que eles gastarem na plataforma.
                Tokens, coins — tudo gera comissão pra você!
              </Typography>
            </Box>

            <Typography sx={{ color: '#D1D5DB', fontWeight: 600, mb: 2, fontSize: '0.95rem' }}>
              Preencha o formulário para solicitar
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
              <TextField fullWidth label="Nome completo" value={form.nome}
                onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} />
              <TextField fullWidth label="Email" value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
              <TextField fullWidth label="WhatsApp" placeholder="(31) 97139-1218" value={form.whatsapp}
                onChange={e => setForm(p => ({ ...p, whatsapp: e.target.value }))} />
              <TextField fullWidth label="Chave Pix" placeholder="CPF, email, telefone ou chave aleatória" value={form.chavePix}
                onChange={e => setForm(p => ({ ...p, chavePix: e.target.value }))} />
            </Box>
            <TextField fullWidth label="Por que quer ser afiliado?" multiline rows={3}
              placeholder="Conte um pouco sobre você e como pretende divulgar..."
              value={form.motivo}
              onChange={e => setForm(p => ({ ...p, motivo: e.target.value }))}
              sx={{ mb: 3 }}
            />

            <Button fullWidth variant="contained" size="large" startIcon={submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <Send size={18} />}
              disabled={submitting} onClick={handleSubmit}
              sx={{ py: 1.5, fontSize: '1rem', background: '#7C3AED', '&:hover': { background: '#6D28D9' } }}
            >
              {submitting ? 'Enviando...' : 'Enviar Solicitação'}
            </Button>
          </SectionCard>
        )}

        {/* Pendente */}
        {isPending && (
          <SectionCard elevation={0}>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Clock size={48} color="#F59E0B" style={{ marginBottom: 16 }} />
              <Typography variant="h6" sx={{ color: '#FCD34D', fontWeight: 700, mb: 1 }}>
                Solicitação em Análise
              </Typography>
              <Typography sx={{ color: '#6B7280', fontSize: '0.9rem' }}>
                Sua solicitação está sendo avaliada. Você será notificado quando for aprovada.
              </Typography>
            </Box>
          </SectionCard>
        )}

        {/* Rejeitado */}
        {isRejected && (
          <SectionCard elevation={0}>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <XCircle size={48} color="#EF4444" style={{ marginBottom: 16 }} />
              <Typography variant="h6" sx={{ color: '#FCA5A5', fontWeight: 700, mb: 1 }}>
                Solicitação Rejeitada
              </Typography>
              <Typography sx={{ color: '#6B7280', fontSize: '0.9rem' }}>
                Infelizmente sua solicitação não foi aprovada desta vez.
              </Typography>
            </Box>
          </SectionCard>
        )}

        {/* Aprovado: Dashboard */}
        {isApproved && (
          <>
            <SectionCard elevation={0}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CheckCircle2 size={20} color="#10B981" />
                <Typography sx={{ color: '#10B981', fontWeight: 700, fontSize: '1rem' }}>
                  Afiliado Ativo
                </Typography>
              </Box>

              <Box sx={{ p: 2, borderRadius: 2, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', mb: 3 }}>
                <Typography sx={{ color: '#6B7280', fontSize: '0.78rem', textTransform: 'uppercase', mb: 0.5 }}>
                  Seu link de indicação
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Link2 size={16} color="#A78BFA" />
                  <Typography sx={{ color: '#F9FAFB', fontWeight: 600, fontSize: '0.88rem', wordBreak: 'break-all', flex: 1 }}>
                    {getRefLink()}
                  </Typography>
                  <Tooltip title={copied === 'link' ? 'Copiado!' : 'Copiar link'}>
                    <IconButton size="small" onClick={() => handleCopy(getRefLink(), 'link')}>
                      {copied === 'link' ? <Check size={16} color="#10B981" /> : <Copy size={16} color="#6B7280" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <StatBox>
                  <Users size={24} color="#7C3AED" style={{ marginBottom: 8 }} />
                  <Typography sx={{ color: '#F9FAFB', fontWeight: 800, fontSize: '1.8rem', lineHeight: 1 }}>
                    {affiliate.totalReferrals || 0}
                  </Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.78rem', mt: 0.5 }}>Indicados</Typography>
                </StatBox>
                <StatBox>
                  <DollarSign size={24} color="#10B981" style={{ marginBottom: 8 }} />
                  <Typography sx={{ color: '#10B981', fontWeight: 800, fontSize: '1.8rem', lineHeight: 1 }}>
                    R$ {(affiliate.totalEarnings || 0).toFixed(2).replace('.', ',')}
                  </Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.78rem', mt: 0.5 }}>Total ganho</Typography>
                </StatBox>
                <StatBox>
                  <Banknote size={24} color="#F59E0B" style={{ marginBottom: 8 }} />
                  <Typography sx={{ color: '#F59E0B', fontWeight: 800, fontSize: '1.8rem', lineHeight: 1 }}>
                    R$ {(affiliate.availableBalance || 0).toFixed(2).replace('.', ',')}
                  </Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.78rem', mt: 0.5 }}>Disponível p/ saque</Typography>
                </StatBox>
              </Box>

              {affiliate.pendingWithdraw ? (
                <Box sx={{ p: 2, borderRadius: 2, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Clock size={20} color="#F59E0B" />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ color: '#FCD34D', fontWeight: 600, fontSize: '0.9rem' }}>
                      Saque de R$ {affiliate.pendingWithdraw.valor?.toFixed(2).replace('.', ',')} em análise
                    </Typography>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.78rem' }}>
                      Solicitado em {new Date(affiliate.pendingWithdraw.createdAt).toLocaleDateString('pt-BR')} — aguardando confirmação
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Button
                  fullWidth variant="contained" size="large"
                  startIcon={withdrawing ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <Banknote size={18} />}
                  disabled={withdrawing || (affiliate.availableBalance || 0) < 1}
                  onClick={handleWithdraw}
                  sx={{
                    py: 1.3, fontSize: '0.95rem',
                    background: (affiliate.availableBalance || 0) >= 1 ? '#10B981' : 'rgba(107,114,128,0.2)',
                    '&:hover': { background: '#059669' },
                    '&.Mui-disabled': { background: 'rgba(107,114,128,0.2)', color: '#4B5563' }
                  }}
                >
                  {withdrawing ? 'Solicitando...'
                    : (affiliate.availableBalance || 0) < 1 ? 'Sem saldo para saque'
                    : `Solicitar Saque — R$ ${(affiliate.availableBalance || 0).toFixed(2).replace('.', ',')}`}
                </Button>
              )}
            </SectionCard>

            {/* Indicados */}
            {affiliate.referrals?.length > 0 && (
              <SectionCard elevation={0}>
                <Typography sx={{ color: '#D1D5DB', fontWeight: 600, mb: 2, fontSize: '0.95rem' }}>
                  Seus Indicados ({affiliate.referrals.length})
                </Typography>
                <TableContainer sx={{ background: '#0D1117', borderRadius: 2, border: '1px solid rgba(255,255,255,0.06)' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#7C3AED', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Nome</TableCell>
                        <TableCell sx={{ color: '#7C3AED', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Email</TableCell>
                        <TableCell sx={{ color: '#7C3AED', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Data</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {affiliate.referrals.map(r => (
                        <TableRow key={r._id}>
                          <TableCell sx={{ color: '#D1D5DB', fontSize: '0.82rem', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>{r.nome}</TableCell>
                          <TableCell sx={{ color: '#D1D5DB', fontSize: '0.82rem', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>{r.email}</TableCell>
                          <TableCell sx={{ color: '#6B7280', fontSize: '0.78rem', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            {r.createdAt ? new Date(r.createdAt).toLocaleDateString('pt-BR') : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </SectionCard>
            )}

            {/* Comissões */}
            {affiliate.commissions?.length > 0 && (
              <SectionCard elevation={0}>
                <Typography sx={{ color: '#D1D5DB', fontWeight: 600, mb: 2, fontSize: '0.95rem' }}>
                  Histórico de Comissões
                </Typography>
                <TableContainer sx={{ background: '#0D1117', borderRadius: 2, border: '1px solid rgba(255,255,255,0.06)' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#7C3AED', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Tipo</TableCell>
                        <TableCell sx={{ color: '#7C3AED', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Valor Compra</TableCell>
                        <TableCell sx={{ color: '#7C3AED', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Comissão</TableCell>
                        <TableCell sx={{ color: '#7C3AED', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Data</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {affiliate.commissions.map(c => (
                        <TableRow key={c._id}>
                          <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <Chip label={c.tipo === 'coins' ? 'Coins' : 'Tokens'} size="small"
                              sx={{ background: c.tipo === 'coins' ? 'rgba(245,158,11,0.15)' : 'rgba(124,58,237,0.15)', color: c.tipo === 'coins' ? '#F59E0B' : '#A78BFA', fontWeight: 600, fontSize: '0.72rem' }} />
                          </TableCell>
                          <TableCell sx={{ color: '#D1D5DB', fontSize: '0.82rem', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            R$ {c.valorCompra?.toFixed(2).replace('.', ',')}
                          </TableCell>
                          <TableCell sx={{ color: '#10B981', fontWeight: 600, fontSize: '0.82rem', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            R$ {c.comissao?.toFixed(2).replace('.', ',')}
                          </TableCell>
                          <TableCell sx={{ color: '#6B7280', fontSize: '0.78rem', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            {c.createdAt ? new Date(c.createdAt).toLocaleDateString('pt-BR') : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </SectionCard>
            )}
          </>
        )}

        <Box sx={{ p: 2.5, borderRadius: 3, background: 'rgba(124,58,237,0.04)', border: '1px solid rgba(124,58,237,0.1)' }}>
          <Typography sx={{ color: '#A78BFA', fontWeight: 600, mb: 1, fontSize: '0.88rem' }}>Como funciona?</Typography>
          <Box component="ol" sx={{ color: '#9CA3AF', fontSize: '0.85rem', pl: 2.5, m: 0, '& li': { mb: 0.5 } }}>
            <li>Solicite para ser afiliado preenchendo o formulário acima</li>
            <li>Após aprovação, você recebe um link de indicação único</li>
            <li>Compartilhe o link com seus amigos</li>
            <li>Quando eles se cadastram pelo seu link e fazem compras, você ganha <strong style={{ color: '#10B981' }}>10%</strong> de comissão</li>
            <li>As comissões são pagas via PIX automático na chave cadastrada</li>
          </Box>
        </Box>
      </Box>
    </Fade>
  );
}
